import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';

/**
 * MessagesInbox
 * --------------
 * A tenant-scoped, role-aware messages history view.
 *
 * - Lists every communication this user can see (their own outgoing
 *   messages, replies, and any direct/child-scoped notifications).
 * - Groups messages by `thread_id` so a parent <-> centre conversation
 *   reads top-to-bottom.
 * - Shows a "New message" CTA which opens the shared MessageComposer.
 * - Each thread supports inline reply via `POST /api/communications/{id}/reply`.
 *
 * Read receipts: when a thread is opened we mark every unread message
 * inside it as read so the bell badge clears.
 */
export const MessagesInbox = ({ user, onCompose }) => {
  const isParent = user?.role === 'parent';
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getCommunications();
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load messages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Build threaded view. The backend stamps `thread_id` on the original
  // message and copies it onto every reply, so grouping is straightforward.
  const threads = useMemo(() => {
    const byThread = new Map();
    for (const m of messages) {
      const key = m.thread_id || m._id;
      if (!byThread.has(key)) byThread.set(key, []);
      byThread.get(key).push(m);
    }
    // Sort each thread oldest -> newest, then sort threads by latest activity
    const threadList = Array.from(byThread.entries()).map(([id, msgs]) => {
      const sorted = msgs.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const latest = sorted[sorted.length - 1];
      const firstRoot = sorted[0];
      const subject = firstRoot.subject || (firstRoot.message || '').slice(0, 60) || '(no subject)';
      const unread = sorted.some(x => !x.is_read && x.sent_by_user_id !== user?._id);
      return {
        id,
        subject,
        child_name: firstRoot.child_name || latest.child_name || null,
        latest,
        messages: sorted,
        unread,
      };
    });
    threadList.sort((a, b) => new Date(b.latest.created_at) - new Date(a.latest.created_at));
    return threadList;
  }, [messages, user]);

  const unreadCount = threads.filter(t => t.unread).length;

  const openThread = async (thread) => {
    setActiveThreadId(thread.id);
    setReply('');
    setError('');
    // Mark every unread message in the thread as read
    const toMark = thread.messages.filter(m => !m.is_read && m.sent_by_user_id !== user?._id);
    if (toMark.length === 0) return;
    try {
      await Promise.all(toMark.map(m => api.markCommunicationRead(m._id).catch(() => null)));
      setMessages(prev => prev.map(m => (
        toMark.some(x => x._id === m._id) ? { ...m, is_read: true } : m
      )));
    } catch (_) { /* non-fatal */ }
  };

  const sendReply = async (thread) => {
    if (!reply.trim()) return;
    setSending(true);
    setError('');
    try {
      // Reply to the LAST message in the thread so the backend captures
      // the most recent context (subject, child_id, recipient swap).
      const head = thread.messages[thread.messages.length - 1];
      await api.replyCommunication(head._id, reply.trim());
      setReply('');
      await load();
    } catch (e) {
      setError(e.message || 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const activeThread = threads.find(t => t.id === activeThreadId) || null;

  return (
    <div data-testid="messages-inbox">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ margin: 0 }}>Messages</h1>
          <p style={{ margin: '0.25rem 0 0', color: 'hsl(var(--muted-foreground))' }}>
            {isParent
              ? 'Your conversation history with your childcare provider.'
              : 'Conversation history with parents and staff.'}
            {unreadCount > 0 && (
              <> · <strong data-testid="messages-unread-count">{unreadCount} unread</strong></>
            )}
          </p>
        </div>
        {onCompose && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={onCompose}
            data-testid="messages-compose-btn"
          >
            ✉️ New message
          </button>
        )}
      </div>

      {error && (
        <div className="card" style={{ background: 'hsl(0 70% 96%)', borderColor: 'hsl(0 70% 75%)', marginBottom: '1rem' }} data-testid="messages-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading messages…</div>
      ) : threads.length === 0 ? (
        <div className="empty-state" data-testid="messages-empty">
          <p>No messages yet.</p>
          {onCompose && (
            <button type="button" className="btn btn-primary" onClick={onCompose} data-testid="messages-empty-compose-btn">
              ✉️ Start a conversation
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 320px) 1fr', gap: '1rem', alignItems: 'flex-start' }}>
          {/* Thread list */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="messages-thread-list">
            {threads.map(t => (
              <div
                key={t.id}
                onClick={() => openThread(t)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') openThread(t); }}
                data-testid={`messages-thread-${t.id}`}
                style={{
                  padding: '0.85rem 1rem',
                  borderBottom: '1px solid hsl(var(--border))',
                  cursor: 'pointer',
                  background: activeThreadId === t.id ? 'hsl(var(--accent))' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                  <strong style={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.subject}
                  </strong>
                  {t.unread && (
                    <span
                      className="badge"
                      style={{ background: 'hsl(220 90% 60%)', color: 'white', fontSize: '0.65rem' }}
                      data-testid={`messages-thread-${t.id}-unread`}
                    >
                      NEW
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                  {t.child_name && <span>· {t.child_name} · </span>}
                  {new Date(t.latest.created_at).toLocaleString()}
                </div>
                <div style={{
                  fontSize: '0.82rem',
                  color: 'hsl(var(--muted-foreground))',
                  marginTop: '0.25rem',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {t.latest.message || ''}
                </div>
              </div>
            ))}
          </div>

          {/* Active thread */}
          {activeThread ? (
            <div className="card" data-testid="messages-thread-detail">
              <div style={{ borderBottom: '1px solid hsl(var(--border))', paddingBottom: '0.75rem', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0 }}>{activeThread.subject}</h3>
                {activeThread.child_name && (
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>About {activeThread.child_name}</small>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: 480, overflowY: 'auto' }}>
                {activeThread.messages.map(m => {
                  const mine = m.sent_by_user_id === user?._id;
                  return (
                    <div
                      key={m._id}
                      data-testid={`messages-msg-${m._id}`}
                      style={{
                        alignSelf: mine ? 'flex-end' : 'flex-start',
                        background: mine ? 'hsl(var(--primary) / 0.12)' : 'hsl(var(--muted))',
                        border: '1px solid hsl(var(--border))',
                        padding: '0.6rem 0.85rem',
                        borderRadius: 12,
                        maxWidth: '85%',
                      }}
                    >
                      <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', marginBottom: 4 }}>
                        <strong>{mine ? 'You' : (m.sent_by_name || 'Provider')}</strong>
                        {' · '}
                        {new Date(m.created_at).toLocaleString()}
                        {m.priority && m.priority !== 'normal' && (
                          <> · <em style={{ textTransform: 'uppercase' }}>{m.priority}</em></>
                        )}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.message}</div>
                    </div>
                  );
                })}
              </div>
              <form
                onSubmit={(e) => { e.preventDefault(); sendReply(activeThread); }}
                style={{ marginTop: '0.85rem', display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}
              >
                <textarea
                  rows={2}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type a reply…"
                  style={{ flex: 1 }}
                  data-testid="messages-reply-input"
                />
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={sending || !reply.trim()}
                  data-testid="messages-reply-send"
                >
                  {sending ? 'Sending…' : 'Reply'}
                </button>
              </form>
            </div>
          ) : (
            <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }} data-testid="messages-empty-detail">
              <p style={{ color: 'hsl(var(--muted-foreground))' }}>Select a conversation to view messages.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MessagesInbox;
